'use client';

import React from 'react';

import './ExperianModal.scss';
import { clsx } from '@libs/common-next';
// @ts-expect-error
import type { AddressProps } from '@petsmart-ui/sparky/dist/components/Address/Address';
import { isLocaleCA } from '@libs/core';
import type { ExperianResponse, ExperianResponseLayout, ValidationDetail } from './types';
import { extractZipCodeFromResponse, extractZipCodeFromResponseLayout } from './utils';

/**
 * Type guard to determine if the response is of type ExperianResponseLayout.
 *
 * This function checks the presence of the `addresses_formatted` property in the response result,
 * which is unique to the ExperianResponseLayout type.
 *
 * @param response - The ExperianResponse or ExperianResponseLayout object to check.
 * @returns True if the response is of type ExperianResponseLayout, otherwise false.
 */
function isExperianResponseLayoutType(
    response: ExperianResponse | ExperianResponseLayout
): response is ExperianResponseLayout {
    return (
        'result' in response &&
        'addresses_formatted' in response.result &&
        Array.isArray(response.result.addresses_formatted)
    );
}

const componentClassname = 'modal-item';

export function ExperianModalItem({
    addressName,
    street,
    extendedAddress,
    city,
    state,
    postalCode,
    validationDetail,
    experianResponse,
}: AddressProps & {
    validationDetail?: ValidationDetail;
    experianResponse?: ExperianResponseLayout | ExperianResponse;
}) {
    const isZipCodeMismatch = () => {
        if (!experianResponse || !validationDetail?.zip_code_corrected) return false;

        const extracted = isExperianResponseLayoutType(experianResponse)
            ? extractZipCodeFromResponseLayout(experianResponse)
            : extractZipCodeFromResponse(experianResponse);

        return Boolean(extracted) && extracted !== postalCode;
    };

    const highlightZipCode = isLocaleCA()
        ? !!experianResponse && validationDetail?.zip_code_corrected
        : isZipCodeMismatch();

    const highlightStreet =
        experianResponse?.result?.confidence === 'Premises partial' ||
        experianResponse?.result?.confidence === 'Street partial' ||
        validationDetail?.street_corrected;

    return (
        <div data-testid="modal-item" className={componentClassname} data-cs-mask>
            <p>{addressName}</p>
            <p className={clsx(highlightStreet && `sparky-u-color-utility-error`)}>{street}</p>
            {!!extendedAddress && <p>{extendedAddress}</p>}
            <p>
                <span className={clsx(validationDetail?.city_name_corrected && `sparky-u-color-utility-error`)}>
                    {city}
                </span>
                ,{' '}
                <span className={clsx(validationDetail?.state_corrected && `sparky-u-color-utility-error`)}>
                    {state}
                </span>{' '}
                <span className={clsx(highlightZipCode && `sparky-u-color-utility-error`)}>{postalCode}</span>
            </p>
        </div>
    );
}
